import {
  BarChart3,
  FerrisWheel,
  Gamepad2,
  Globe,
  Headphones,
  Landmark,
  Megaphone,
  Mountain,
  QrCode,
  Rocket,
  Settings,
  Ship,
  Tent,
  Ticket,
  TrendingUp,
  Users,
  WalletCards,
  Waves
} from 'lucide-react';

export const data = {
  heroData: {
    heading: <>Launch Your Waterpark <br className="hidden lg:block" /><span className="">Ticketing With Greet</span></>,
    subHeading: "Launch your waterpark, gaming zone, or amusement park with a complete ticketing, booking, and visitor management platform built for modern attractions.",
    badgeText: "Now onboarding",
    features: [
      { text: 'Launch Your Platform in 24 Hours', icon: Rocket },
      { text: 'Custom Website & Booking Included', icon: Globe },
      { text: 'Complete Online & Offline Ticketing', icon: Ticket },
      { text: 'No Software Development Cost', icon: WalletCards },
      { text: 'Fast QR-Based Visitor Entry', icon: QrCode },
      { text: 'Free Marketing & Growth Support', icon: Megaphone },
    ],
    form: {
      title: "What are you looking for?",
      textAreaPlaceholder: "Ticketing setup, website development, visitor management, or marketing support.",
    }
  },
  problemData: {
    heading: "Launching Your Attraction Shouldn’t Be Complicated",
    subHeading: <>Launching a new attraction comes with enough operational challenges already. Building custom software, managing multiple vendors, and handling manual ticketing systems can slow down your launch and increase costs.<br />Greet helps waterparks, amusement parks, and attractions launch quickly with a ready-to-use ticketing and visitor management platform.</>,
    painPoints: [
      'Expensive custom software development',
      'Delays in launching online ticket booking',
      'Multiple systems for ticketing & website',
      'Manual entry management & long queues',
      'Complex setup requiring technical teams',
      'No centralized visitor management',
      'Lack of marketing guidance for initial growth',
      'Poor technical support post-launch',
    ],
    footerText: "Greet removes all these barriers - so you can focus on running your attraction, not building technology."
  },
  featureData: {
    heading: "Everything Your Attraction Needs to Launch & Grow",
    subHeading: "From ticketing to operations, marketing to support - Greet gives waterparks, gaming zones, and amusement parks one complete platform to run and grow their attraction.",
    features: [
      { id: 1, name: 'Go Live Within 24 Hours', desc: 'Launch your online bookings and visitor operations quickly without technical setup delays.', icon: Rocket },
      { id: 2, name: 'No Software Development Cost', desc: 'Avoid expensive custom software and launch with a ready-to-use attraction platform.', icon: WalletCards },
      { id: 3, name: 'Website & Ticketing Included', desc: 'Get a personalized website and integrated booking system designed for modern attractions.', icon: Globe },
      { id: 4, name: 'Complete Online & Offline Ticketing', desc: 'Manage online bookings, counter sales, group bookings, and walk-ins from one platform.', icon: Ticket },
      { id: 5, name: 'Fast QR-Based Visitor Entry', desc: 'Enable smoother visitor check-ins and reduce queues during peak operational hours.', icon: QrCode },
      { id: 6, name: 'Real-Time Sales & Analytics', desc: 'Track bookings, visitor trends, and revenue performance through a live dashboard and app.', icon: BarChart3 },
      { id: 7, name: 'Marketing & Growth Support', desc: 'Get marketing guidance, promotions support, and visibility assistance to attract more visitors.', icon: Megaphone },
      { id: 8, name: 'Social Media & Paid Ads Support', desc: 'Reach more customers through social media campaigns and digital ads.', icon: TrendingUp },
      { id: 9, name: 'Lifetime Tech & Operational Support', desc: 'From updates to operational assistance, our team stays connected long after launch.', icon: Headphones },
    ]
  },
  industriesData: {
    heading: "Who We Help Launch",
    subHeading: "From waterparks and gaming zones to amusement parks and adventure attractions, Greet helps businesses manage bookings, visitor entry, and operations through one unified platform.",
    industries: [
      { title: 'Waterparks', description: 'Manage high-footfall visitor bookings and entry operations smoothly.', icon: Waves },
      { title: 'Amusement Parks', description: 'Simplify ticketing, crowd management, and visitor experiences.', icon: FerrisWheel },
      { title: 'Gaming Zones & Arcades', description: 'Manage walk-ins, bookings, and visitor operations seamlessly.', icon: Gamepad2 },
      { title: 'Adventure Parks', description: 'Handle activities, ticketing, and visitor entry from one platform.', icon: Mountain },
      { title: 'Family Entertainment Centers', description: 'Streamline bookings and operations for indoor entertainment spaces.', icon: Users },
      { title: 'Museums & Attractions', description: 'Improve visitor management and digital booking experiences.', icon: Landmark },
      {         title: 'Adventure Trips & Tour Packages',
        description: 'Manage bookings, itineraries, group experiences, and traveler operations seamlessly.', icon: Tent },
      { title: 'Water Sports & Activities', description: 'Manage activity bookings, visitor flow, and operational coordination smoothly.', icon: Ship },
    ]
  },
  primaryCtaData: {
    heading: "Ready to experience the difference?",
    buttonText: "Let's Talk, We're Way Better"
  },
  metricsData: {
    metrics: [
      { value: '10M+', label: 'Transactions' },
      { value: '1,00,000+', label: 'Tickets Sold' },
      { value: '5,000+', label: 'Successful Events Managed' },
      { value: '35+', label: 'Cities & Counting', desc: 'Trusted by organizers across India.' },
      { value: '50+', label: 'Live Attractions', desc: 'Waterparks, adventure parks, etc.' },
      { value: '4.8★', label: 'Satisfaction Score', desc: 'Backed by real organizers and institutions.' },
    ]
  },
  testimonialsData: {
    heading: "What Attraction Owners Say About Greet",
    subHeading: "Real experiences from waterparks, amusement parks, and attractions using Greet to manage ticketing, operations, and visitor experiences.",
    testimonials: [
      { id: 1, title: 'Launching our online booking system was much easier than expected.', description: 'Greet helped us set up ticketing, visitor entry, and our website within days. The team handled everything smoothly, and the support has been excellent throughout.', author: 'Operations Manager', role: 'Exotica Club', rating: 5 },
      { id: 2, title: 'The QR entry system made weekends much smoother.', description: 'Earlier, we struggled with long queues during peak hours. After switching to Greet, visitor entry became much faster and easier to manage.', author: 'Owner', role: 'Entertainment Kingdom', rating: 5 },
      { id: 3, title: "We didn't need to hire any developers.", description: 'The biggest advantage for us was that Greet already had a ready platform built for attractions. We launched quickly without spending lakhs on software development.', author: 'Founder', role: 'Akhela Water Sports', rating: 5 },
      { id: 4, title: 'Their support team actually stays connected.', description: 'Most companies disappear after onboarding. With Greet, their operations and tech team still helps us with pricing updates, website changes, and day-to-day support whenever needed.', author: 'Manager', role: 'SSB Kingdom', rating: 5 },
      { id: 5, title: 'The analytics dashboard helps us track everything live.', description: "We can now monitor ticket sales, visitor flow, and peak hours directly from the app. It's helped us make much better operational decisions.", author: 'Manager', role: 'Tourist Attraction & Park', rating: 5 },
      { id: 6, title: 'Greet also helped us improve our online visibility.', description: 'Apart from ticketing, their team guided us with promotions and digital marketing support which helped increase our bookings during weekends and holidays.', author: 'Marketing Head', role: 'SSB Gamezone', rating: 5 },
      { id: 7, title: 'Managing online and offline ticket sales became simple.', description: 'Before Greet, we used separate systems for counter sales and online bookings. Now everything is managed from one platform, which saves our team a lot of time.', author: 'Operations Team', role: 'Theme Park', rating: 5 },
      { id: 8, title: 'The platform handled heavy visitor traffic smoothly.', description: 'During festivals and holiday rush, the system remained fast and stable. That reliability is very important for attractions like ours.', author: 'Owner', role: 'Seasonal Attraction & Waterpark', rating: 5 },
    ]
  },
  comparisonData: {
    heading: "How Greet Stands Out",
    subHeading: "A quick comparison of Greet vs traditional ticketing platforms.",
    comparisons: [
      { feature: 'Launch Time', icon: Rocket, greet: 'Go live within 24 hours', competitor: 'Weeks or months of setup' },
      { feature: 'Website & Booking', icon: Globe, greet: 'Personalized website + ticketing included', competitor: 'Requires multiple vendors or agencies' },
      { feature: 'Ticketing System', icon: Ticket, greet: 'Complete online & offline ticketing', competitor: 'Limited or fragmented ticketing systems' },
      { feature: 'Visitor Entry', icon: QrCode, greet: 'Fast QR-based visitor management', competitor: 'Slower entry and manual bottlenecks' },
      { feature: 'Pricing', icon: WalletCards, greet: 'No software development cost', competitor: 'Expensive setup and maintenance charges' },
      { feature: 'Analytics & Insights', icon: BarChart3, greet: 'Real-time dashboard and mobile app insights', competitor: 'Basic or delayed reporting systems' },
      { feature: 'Marketing Support', icon: Megaphone, greet: 'Marketing and growth support included', competitor: 'Usually unavailable or charged separately' },
      { feature: 'Lifetime Support', icon: Headphones, greet: 'Lifetime tech & operational support', competitor: 'Limited onboarding or ticket-based support' },
      { feature: 'Operations Management', icon: Settings, greet: 'Dedicated support for pricing, setup & operations', competitor: 'Mostly self-managed or extra paid support' },
      { feature: 'Scalability', icon: TrendingUp, greet: 'Built for high-footfall attractions', competitor: 'May struggle during peak traffic days' },
    ]
  },
  secondaryCtaData: {
    heading: "Ready to Launch Your Attraction Online?",
    subHeading: "Launch your website, ticketing, visitor entry, and operations platform quickly with Greet - built for waterparks and modern attractions.",
    buttonText: "Talk to Our Team"
  },
  eventsData: {
    events: [
      {
        id: 1,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1744638553397_1706606350134_Get%252520High%252520on%252520Entertainment%25252010%252520by%25252020%252520feet%252520copy%252520%2525281%252529.webp&w=1080&q=75",
        name: "Entertainment Kingdom Sambalpur",
        eventAddress: "Silat, Odisha",
        link: "https://greet.fydo.in/entertainment-kingdom-sambalpur",
      },
      {
        id: 2,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1746426864119_SSB%2520waterpark_25x10%2520%25282%2529%2520%25281%2529.jpg&w=1080&q=75",
        name: "SSB Kingdom Water Park",
        eventAddress: "Ghodabara, Odisha",
        link: "https://greet.fydo.in/ssb-kingdom-water-park",
      },
      {
        id: 3,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1749827612515_Exotica%2520Aloha%2521%252025X10.jpg&w=1080&q=75",
        name: "TheExoticaAIohaClub",
        eventAddress: "Cuttack, Odisha",
        link: "https://greet.fydo.in/theexoticaalohaclub",
      },
      {
        id: 4,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1746248345794_SSB%2520Game%2520zone_25x10%2520%25281%2529%2520%25281%2529.jpg&w=1080&q=75",
        name: "SSB Game Zone",
        eventAddress: "Cuttack, Odisha",
        link: "https://greet.fydo.in/ssb-game-zone",
      },
      {
        id: 5,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1745409204479_banner-45x20-v1-aprl-22.jpg&w=1080&q=75",
        name: "Blue Splash Water Park Puri",
        eventAddress: "Puri, Odisha",
        link: "https://greet.fydo.in/blue-splash-water-park",
      },
      {
        id: 6,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1738946798438_WhatsApp%2520Image%25202025-02-07%2520at%25207.47.52%2520PM.jpeg&w=1080&q=75",
        name: "Akhela Water Sports",
        eventAddress: "Sambalpur, Odisha",
        link: "https://greet.fydo.in/akhela-water-sports",
      },
    ],
  }
};
